import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CheckCircleIcon,
  PrinterIcon,
  ArrowDownTrayIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';
import { Modal, Button } from '../ui';
import { paymentsAPI } from '../../services/api';
import toast from 'react-hot-toast';

const METHOD_LABELS: Record<string, string> = {
  EFECTIVO: 'Efectivo',
  TRANSFERENCIA_BANCARIA: 'Transferencia Bancaria',
  TRANSFERENCIA_MOVIL: 'Transferencia Móvil',
  CHEQUE: 'Cheque',
  DEPOSITO_BANCARIO: 'Depósito Bancario',
};

export interface PaymentSuccessAllocation {
  amountAppliedMinor?: number;
  invoice?: {
    id: number;
    invoiceNumber?: string;
    status?: string;
    balanceDueMinor?: number;
  } | null;
  invoiceId?: number;
}

export interface PaymentSuccessData {
  id: number;
  amount?: number;
  amountMinor?: number;
  paymentMethod?: string;
  paymentDate?: string;
  reference?: string | null;
  bankReference?: string | null;
  status?: string;
  client?: {
    id?: number;
    name?: string;
    lastname?: string;
  } | null;
  allocations?: PaymentSuccessAllocation[];
}

interface PaymentSuccessModalProps {
  isOpen: boolean;
  onClose: () => void;
  payment: PaymentSuccessData | null;
  creditBalanceMinor?: number;
}

const fmtMoney = (minor?: number, major?: number) => {
  const v = minor != null ? minor / 100 : Number(major || 0);
  return `$${v.toLocaleString('es-DO', { minimumFractionDigits: 2 })}`;
};

const fmtDate = (d?: string) =>
  d
    ? new Date(d).toLocaleDateString('es-DO', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      })
    : '—';

const PaymentSuccessModal: React.FC<PaymentSuccessModalProps> = ({
  isOpen,
  onClose,
  payment,
  creditBalanceMinor = 0,
}) => {
  const navigate = useNavigate();
  const [downloading, setDownloading] = useState(false);

  if (!payment) return null;

  const allocations = payment.allocations || [];
  const invoices = allocations
    .map((a) => a.invoice)
    .filter((inv): inv is NonNullable<typeof inv> => Boolean(inv?.id));

  const clientName = payment.client
    ? `${payment.client.name || ''} ${payment.client.lastname || ''}`.trim() || '—'
    : '—';

  const handlePrintInvoice = (invoiceId: number) => {
    onClose();
    navigate(`/invoices/${invoiceId}/print`);
  };

  const handleDownloadReceipt = async () => {
    setDownloading(true);
    try {
      const response = await paymentsAPI.downloadReceipt(payment.id);
      const url = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `recibo_pago_${payment.id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success('Recibo descargado');
    } catch (error: any) {
      toast.error('Error al descargar recibo: ' + (error.response?.data?.error || error.message));
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Pago registrado" size="md">
      <div className="space-y-5">
        <div className="flex items-start gap-3 rounded-xl bg-emerald-50 border border-emerald-100 px-4 py-3">
          <CheckCircleIcon className="h-6 w-6 text-emerald-600 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-emerald-900">El pago se registró correctamente</p>
            <p className="text-xs text-emerald-700 mt-0.5">
              Revisa los detalles o imprime la factura asociada.
            </p>
          </div>
        </div>

        <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
          <div>
            <dt className="text-xs text-slate-500">Cliente</dt>
            <dd className="font-medium text-slate-900 mt-0.5">{clientName}</dd>
          </div>
          <div>
            <dt className="text-xs text-slate-500">Monto</dt>
            <dd className="font-semibold text-slate-900 mt-0.5">
              {fmtMoney(payment.amountMinor, payment.amount)}
            </dd>
          </div>
          <div>
            <dt className="text-xs text-slate-500">Método</dt>
            <dd className="font-medium text-slate-900 mt-0.5">
              {METHOD_LABELS[payment.paymentMethod || ''] || payment.paymentMethod || '—'}
            </dd>
          </div>
          <div>
            <dt className="text-xs text-slate-500">Fecha</dt>
            <dd className="font-medium text-slate-900 mt-0.5">{fmtDate(payment.paymentDate)}</dd>
          </div>
          {(payment.reference || payment.bankReference) && (
            <div className="col-span-2">
              <dt className="text-xs text-slate-500">Referencia</dt>
              <dd className="font-medium text-slate-900 mt-0.5">
                {payment.bankReference || payment.reference}
              </dd>
            </div>
          )}
          {creditBalanceMinor > 0 && (
            <div className="col-span-2">
              <dt className="text-xs text-slate-500">Saldo a crédito</dt>
              <dd className="font-medium text-amber-700 mt-0.5">{fmtMoney(creditBalanceMinor)}</dd>
            </div>
          )}
        </dl>

        {invoices.length > 0 && (
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">
              Facturas aplicadas
            </p>
            <ul className="space-y-2">
              {allocations.map((alloc, idx) => {
                const inv = alloc.invoice;
                if (!inv?.id) return null;
                const applied = alloc.amountAppliedMinor;
                return (
                  <li
                    key={`${inv.id}-${idx}`}
                    className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 px-3 py-2.5"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <DocumentTextIcon className="h-4 w-4 text-slate-400 shrink-0" />
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-slate-800 truncate">
                          {inv.invoiceNumber || `Factura #${inv.id}`}
                        </p>
                        {applied != null && (
                          <p className="text-xs text-slate-500">Aplicado: {fmtMoney(applied)}</p>
                        )}
                      </div>
                    </div>
                    {invoices.length > 1 && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePrintInvoice(inv.id)}
                      >
                        <PrinterIcon className="h-4 w-4 mr-1" />
                        Imprimir
                      </Button>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-2 pt-1">
          {invoices.length === 1 && (
            <Button
              variant="danger"
              size="md"
              className="flex-1"
              onClick={() => handlePrintInvoice(invoices[0].id)}
            >
              <PrinterIcon className="h-4 w-4 mr-2" />
              Imprimir factura
            </Button>
          )}
          <Button
            variant="outline"
            size="md"
            className="flex-1"
            onClick={handleDownloadReceipt}
            loading={downloading}
          >
            <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
            Descargar recibo
          </Button>
          <Button variant="outline" size="md" onClick={onClose}>
            Cerrar
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default PaymentSuccessModal;
